// Pure display helpers for patient demographics. No React, no I/O — kept here
// as the single source of truth so the result row, header band and summary all
// compute age/sex identically.

import type { PatientSex } from '@/types/patients';

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Whole-years age from a `yyyy-mm-dd` date of birth, computed against
 * `todayMs` (injectable for tests; defaults to now). Returns null when the DOB
 * is absent or malformed — callers omit the age segment in that case rather
 * than rendering a bogus number.
 */
export function ageFromDob(
  dateOfBirth: string | null | undefined,
  todayMs: number = Date.now(),
): number | null {
  if (!dateOfBirth) return null;
  const m = ISO_DATE_RE.exec(dateOfBirth);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const today = new Date(todayMs);
  let age = today.getUTCFullYear() - year;
  const beforeBirthday =
    today.getUTCMonth() + 1 < month ||
    (today.getUTCMonth() + 1 === month && today.getUTCDate() < day);
  if (beforeBirthday) age -= 1;
  if (age < 0 || age > 150) return null;
  return age;
}

/**
 * Locale-agnostic compact date: `dd MMM yyyy` in the user's locale (e.g.
 * "05 Jun 2026"). The single source of truth for short dates across the visit
 * timeline, visit summary and the registry profile — so they never drift.
 */
export function formatShortDate(d: Date): string {
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Format a calendar date (`yyyy-mm-dd`) as `dd MMM yyyy` in the user's locale,
 * pinned to UTC so the day never shifts. Unlike {@link formatShortDate} (which
 * takes an instant `Date`), this is for date-only fields like date of birth that
 * are stored as strings precisely to avoid a timezone. Returns null for absent
 * or malformed input so callers can omit the field.
 */
export function formatIsoDate(dateOnly: string | null | undefined): string | null {
  if (!dateOnly) return null;
  const m = ISO_DATE_RE.exec(dateOnly);
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** i18n key for a patient's sex pill/label. */
export function sexLabelKey(sex: PatientSex): string {
  switch (sex) {
    case 'male':
      return 'patients.sexMale';
    case 'female':
      return 'patients.sexFemale';
    case 'other':
      return 'patients.sexOther';
    default:
      return 'patients.sexUnknown';
  }
}
